import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

export default class extends Controller {
  static values = {
    lat: Number,
    lng: Number,
    apiKey: String,
  };

  static targets = ["address", "latitude", "longitude", "banner"];

  connect() {
    // Flag to ensure the server is updated at least once when the page mounts
    this.hasPingedServer = false;

    // MODE 1: Google Places Autocomplete (Form Mode)
    if (this.hasAddressTarget) {
      this.loadGoogleMapsScript();
      return;
    }

    // MODE 2: Continuous GPS Tracking (Radar Mode)
    // Runs unconditionally to catch real-time adjustments while tracking movement
    this.startWatchingPosition();
  }

  disconnect() {
    // CRITICAL: Clear tracking hardware cycles when exiting the current context
    if (this.watchId) {
      navigator.geolocation.clearWatch(this.watchId);
    }
  }

  // --- FORM MODE LOGIC ---
  loadGoogleMapsScript() {
    if (typeof google !== "undefined" && google.maps && google.maps.places) {
      this.initGoogleAutocomplete();
      return;
    }
    if (document.querySelector('script[src*="maps.googleapis.com/maps/api"]')) {
      window.initGoogleMaps = this.initGoogleAutocomplete.bind(this);
      return;
    }
    window.initGoogleMaps = this.initGoogleAutocomplete.bind(this);

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKeyValue}&libraries=places&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  initGoogleAutocomplete() {
    this.autocomplete = new google.maps.places.Autocomplete(
      this.addressTarget,
      { types: ["geocode", "establishment"] },
    );
    this.addressTarget.addEventListener("keydown", (e) => {
      if (e.key === "Enter") e.preventDefault();
    });
    this.autocomplete.addListener(
      "place_changed",
      this.placeSelected.bind(this),
    );
  }

  placeSelected() {
    const place = this.autocomplete.getPlace();
    if (!place.geometry) return;
    if (this.hasLatitudeTarget)
      this.latitudeTarget.value = place.geometry.location.lat();
    if (this.hasLongitudeTarget)
      this.longitudeTarget.value = place.geometry.location.lng();
  }

  // --- RADAR MODE LOGIC ---
  startWatchingPosition() {
    if (window.AndroidLocationBridge) {
      window.AndroidLocationBridge.requestLocation();
      return;
    }

    if (!navigator.geolocation) return;

    // Track physical movement independently of the URL
    this.baselinePhysicalLat = null;
    this.baselinePhysicalLng = null;

    this.watchId = navigator.geolocation.watchPosition(
      this.successCallback.bind(this),
      this.errorCallback.bind(this),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }

  successCallback(position) {
    const currentLat = position.coords.latitude;
    const currentLng = position.coords.longitude;

    // 1. Set JS Session Storage for local distance calculation
    sessionStorage.setItem("user_latitude", currentLat);
    sessionStorage.setItem("user_longitude", currentLng);

    // 2. Dispatch event to update distance cards (Flagged 'js' to prevent native infinite loops)
    window.dispatchEvent(
      new CustomEvent("user-location-locked", {
        detail: { lat: currentLat, lng: currentLng, source: "js" },
      }),
    );

    if (this.hasBannerTarget) {
      this.bannerTarget.classList.add("hidden");
    }

    // 3. 🛑 THE FIX: Securely set Cookies so Rails can inherently read them without URL manipulation
    document.cookie = `user_latitude=${currentLat.toFixed(6)}; path=/; max-age=86400`;
    document.cookie = `user_longitude=${currentLng.toFixed(6)}; path=/; max-age=86400`;

    // 4. Track the physical displacement in sessionStorage across Turbo visits
    const lastLoadedLat = parseFloat(sessionStorage.getItem("last_loaded_lat"));
    const lastLoadedLng = parseFloat(sessionStorage.getItem("last_loaded_lng"));

    if (!lastLoadedLat || !lastLoadedLng) {
      // SCENARIO A: First GPS lock of the session
      sessionStorage.setItem("last_loaded_lat", currentLat);
      sessionStorage.setItem("last_loaded_lng", currentLng);

      if (!this.hasPingedServer) {
        this.saveLocationToDatabase(currentLat, currentLng);
        this.hasPingedServer = true;
      }

      // Reload cleanly WITHOUT mutating the URL. Hotwire Native Android respects this!
      Turbo.cache.clear();
      Turbo.visit(window.location.href, { action: "replace" });
      return;
    }

    // SCENARIO B: Continuous Tracking. Did they walk > 150m from the last load?
    const distanceDeltaLat = Math.abs(lastLoadedLat - currentLat);
    const distanceDeltaLng = Math.abs(lastLoadedLng - currentLng);

    if (distanceDeltaLat > 0.0015 || distanceDeltaLng > 0.0015) {
      sessionStorage.setItem("last_loaded_lat", currentLat);
      sessionStorage.setItem("last_loaded_lng", currentLng);

      this.saveLocationToDatabase(currentLat, currentLng);

      // Force a seamless UI update
      Turbo.cache.clear();
      Turbo.visit(window.location.href, { action: "replace" });
    }
  }

  errorCallback(error) {
    console.warn(
      `[SaudaChat Native Link] Geolocation error (${error.code}): ${error.message}`,
    );
    if (
      error.code === error.PERMISSION_DENIED ||
      error.code === error.POSITION_UNAVAILABLE
    ) {
      if (this.hasBannerTarget) {
        this.bannerTarget.classList.remove("hidden");
        this.bannerTarget.classList.add("flex");
      }
    }
  }

  retryPrompt(event) {
    event.preventDefault();
    this.startWatchingPosition();
  }

  // ---------------------------------------------------------
  // Silent Fetch API to update PostGIS Web Radar
  // ---------------------------------------------------------
  saveLocationToDatabase(lat, lng) {
    const csrfMetaTag = document.querySelector('meta[name="csrf-token"]');
    if (!csrfMetaTag) return;

    fetch("/update_location", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfMetaTag.content,
      },
      body: JSON.stringify({ latitude: lat, longitude: lng }),
    })
      .then((response) => {
        if (response.ok)
          console.log("Web Radar: Coordinates securely updated on server.");
      })
      .catch((error) => console.error("Error updating Web Radar:", error));
  }

  handleNativeLocationUpdate(event) {
    // 🛑 INFINITE LOOP PROTECTION: If this event was dispatched by our own JS above, ignore it!
    if (event.detail && event.detail.source === "js") return;

    const mockPosition = {
      coords: {
        latitude: event.detail.lat,
        longitude: event.detail.lng,
      },
    };

    this.successCallback(mockPosition);
  }
}
